/**
 * Phase 15 — Component Promotion Engine (orchestration)
 *
 * Client-side helper that triggers the server-side `evaluate_component_promotions`
 * RPC. The actual decision math lives in `promotionScoring.ts` and its mirror
 * inside the SQL function (single source of truth).
 *
 * This module exists so UI code never calls the RPC directly.
 */
import { supabase } from '@/integrations/supabase/client';

export interface PromotionEngineRunResult {
  shop_id: string;
  component: string;
  condition_type: string;
  decision: 'promote' | 'hold' | 'demote' | 'hard_demote';
  promotion_score: number;
  regression_score: number;
}

/**
 * Triggers the promotion evaluator. Pass a shop_id to scope; pass null to run
 * shop-wide. Returns the per-component decisions for the latest run.
 */
export async function runPromotionEngine(
  shopId: string | null = null,
  component: string | null = null,
): Promise<PromotionEngineRunResult[]> {
  const { data, error } = await supabase.rpc('evaluate_component_promotions' as any, {
    p_shop_id: shopId,
    p_component: component,
  });
  if (error) throw error;
  return (data ?? []) as PromotionEngineRunResult[];
}

/** Manager-only: revert a component to its previous stage. */
export async function revertComponentPromotion(
  stateId: string,
  reason = 'manager_revert',
): Promise<{ ok: boolean; error?: string }> {
  const { data, error } = await supabase.rpc('revert_component_promotion' as any, {
    p_state_id: stateId,
    p_reason: reason,
  });
  if (error) return { ok: false, error: error.message };
  return data as { ok: boolean; error?: string };
}
